'use client';

// Snapshot of browser / device / app state captured at problem-report time.
// Bundled into reports so support staff can rule out client-side issues
// (dark mode rendering, screen size, browser version, network, etc.) without
// having to ask the user follow-up questions.

export interface ClientContext {
  // App
  app_version?: string;
  release_stage?: string;
  pathname: string;
  url: string;
  referrer?: string;

  // Browser
  user_agent: string;
  language?: string;
  languages?: string[];
  timezone?: string;
  cookies_enabled?: boolean;

  // Display / theme
  theme: 'dark' | 'light' | 'unknown';
  prefers_dark: boolean;
  prefers_reduced_motion: boolean;
  color_scheme_meta?: string;
  viewport: { width: number; height: number; dpr: number };
  screen: { width: number; height: number; available_width: number; available_height: number };

  // Network
  online: boolean;
  effective_connection_type?: string;
  downlink_mbps?: number;
  rtt_ms?: number;

  // Storage / session
  local_storage_keys_count?: number;

  // Engine
  platform?: string;
  hardware_concurrency?: number;
  device_memory_gb?: number;

  // Captured time
  captured_at_iso: string;
}

/**
 * Read the user's effective theme — looks at the standard `dark` class on
 * <html> first (Tailwind's class-based dark mode setup), then falls back
 * to the prefers-color-scheme media query.
 */
function detectTheme(): { theme: ClientContext['theme']; prefersDark: boolean } {
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return { theme: 'unknown', prefersDark: false };
  }
  const prefersDark = !!window.matchMedia?.('(prefers-color-scheme: dark)').matches;
  const htmlEl = document.documentElement;
  if (htmlEl.classList.contains('dark')) return { theme: 'dark', prefersDark };
  if (htmlEl.classList.contains('light')) return { theme: 'light', prefersDark };
  // No explicit class → fall back to system preference
  return { theme: prefersDark ? 'dark' : 'light', prefersDark };
}

/** Best-effort access to `navigator.connection` (Chromium-only). */
function readNetworkInfo(): Pick<ClientContext, 'effective_connection_type' | 'downlink_mbps' | 'rtt_ms'> {
  if (typeof navigator === 'undefined') return {};
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const c: any = (navigator as any).connection || (navigator as any).mozConnection || (navigator as any).webkitConnection;
  if (!c) return {};
  return {
    effective_connection_type: typeof c.effectiveType === 'string' ? c.effectiveType : undefined,
    downlink_mbps: typeof c.downlink === 'number' ? c.downlink : undefined,
    rtt_ms: typeof c.rtt === 'number' ? c.rtt : undefined,
  };
}

/**
 * Capture a snapshot of the user's browser + display + network state for
 * inclusion in a problem report. Safe to call on the server (returns an
 * empty-ish object); meaningful values are populated client-side.
 */
export function collectClientContext(extra: Partial<ClientContext> = {}): ClientContext {
  if (typeof window === 'undefined') {
    return {
      pathname: '',
      url: '',
      user_agent: '',
      theme: 'unknown',
      prefers_dark: false,
      prefers_reduced_motion: false,
      viewport: { width: 0, height: 0, dpr: 1 },
      screen: { width: 0, height: 0, available_width: 0, available_height: 0 },
      online: false,
      captured_at_iso: new Date().toISOString(),
      ...extra,
    };
  }

  const { theme, prefersDark } = detectTheme();
  const colorSchemeMeta = document.querySelector('meta[name="color-scheme"]')?.getAttribute('content') ?? undefined;
  const network = readNetworkInfo();

  let localStorageKeysCount: number | undefined;
  try { localStorageKeysCount = window.localStorage?.length; } catch { /* private mode / blocked */ }

  return {
    app_version: process.env.APP_VERSION || undefined,
    release_stage:
      process.env.NEXT_PUBLIC_RELEASE_STAGE ||
      process.env.NEXT_PUBLIC_VERCEL_ENV ||
      (process.env.NODE_ENV === 'production' ? 'production' : 'development'),
    pathname: window.location.pathname + window.location.search,
    url: window.location.href,
    referrer: document.referrer || undefined,

    user_agent: navigator.userAgent,
    language: navigator.language,
    languages: Array.isArray(navigator.languages) ? Array.from(navigator.languages) : undefined,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    cookies_enabled: navigator.cookieEnabled,

    theme,
    prefers_dark: prefersDark,
    prefers_reduced_motion: !!window.matchMedia?.('(prefers-reduced-motion: reduce)').matches,
    color_scheme_meta: colorSchemeMeta,
    viewport: {
      width: window.innerWidth,
      height: window.innerHeight,
      dpr: window.devicePixelRatio || 1,
    },
    screen: {
      width: window.screen.width,
      height: window.screen.height,
      available_width: window.screen.availWidth,
      available_height: window.screen.availHeight,
    },

    online: navigator.onLine,
    ...network,

    local_storage_keys_count: localStorageKeysCount,

    platform: navigator.platform,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    hardware_concurrency: (navigator as any).hardwareConcurrency,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    device_memory_gb: (navigator as any).deviceMemory,

    captured_at_iso: new Date().toISOString(),
    ...extra,
  };
}
